import React from "react";
import DragNumberInput from "../../ui/DragNumberInput";
import FieldLabel from "../../ui/FieldLabel";
import MenuSelect from "../../ui/MenuSelect";

export const Row = ({ label, children }) => (
  <div className="px-3 py-1.5">
    <FieldLabel as="div" className="mb-1 text-gray-400 select-none">
      {label}
    </FieldLabel>
    <div className="flex w-full min-w-0 items-center gap-2">{children}</div>
  </div>
);

export const numberOrFallback = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampNumber = (value, fallback, min, max) => {
  const parsed = Number(value);
  return Math.max(min, Math.min(max, Number.isFinite(parsed) ? parsed : fallback));
};

const roundToDecimals = (value, decimals) => Number(value.toFixed(decimals));

export const clampedDecimal = (value, fallback, min, max, decimals) =>
  roundToDecimals(clampNumber(value, fallback, min, max), decimals);

export const FLAG_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

export const AUTOLIMITS_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

export const SOLVER_OPTIONS = [
  { value: "Newton", label: "Newton" },
  { value: "CG", label: "CG" },
  { value: "PGS", label: "PGS" },
];

export const CONE_OPTIONS = [
  { value: "pyramidal", label: "pyramidal" },
  { value: "elliptic", label: "elliptic" },
];

export const JACOBIAN_OPTIONS = [
  { value: "auto", label: "auto" },
  { value: "dense", label: "dense" },
  { value: "sparse", label: "sparse" },
];

export function PanelNumberInput({
  label,
  value,
  onChange,
  min,
  max,
  step = "any",
  disabled,
  className = "w-full",
}) {
  return (
    <DragNumberInput
      label={label}
      value={value}
      onChange={onChange}
      disabled={disabled}
      wrapperClassName={`min-w-0 ${className}`}
      labelClassName="sr-only"
      inputWrapperClassName="w-full"
      inputClassName="h-7 w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm leading-5 disabled:cursor-not-allowed disabled:opacity-50"
      inputProps={{
        step,
        min,
        max,
        "aria-label": label,
        "aria-disabled": disabled || undefined,
      }}
      stepperPlacement="inline"
    />
  );
}

export const NullableNumberInput = ({
  value,
  onChange,
  min,
  max,
  step = "any",
  disabled,
  label = "Value",
  className = "w-full",
}) => (
  <PanelNumberInput
    label={label}
    value={value ?? ""}
    min={min}
    max={max}
    step={step}
    disabled={disabled}
    className={className}
    onChange={(e) => {
      const raw = e.target.value;
      if (raw === "") {
        onChange(null);
        return;
      }
      const parsed = Number(raw);
      onChange(Number.isFinite(parsed) ? parsed : null);
    }}
  />
);

export const FlagSelect = ({ value, onChange, options = FLAG_OPTIONS, disabled, label }) => (
  <MenuSelect
    size="compact"
    className="w-full min-w-0"
    value={value}
    onChange={onChange}
    disabled={disabled}
    options={options}
    triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
    aria-label={label}
  />
);
